import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowRight,
  BookOpen,
  Building2,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Clock,
  GraduationCap,
  Library,
  Loader2,
  LogIn,
  Search,
  Sparkles,
  Star,
  Users
} from 'lucide-react';
import { getImageUrl } from '../utils/imageUtils';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://api.intuitlms.online/api';

const bannerThemes = [
  'from-blue-700 via-sky-500 to-cyan-300',
  'from-slate-950 via-indigo-800 to-blue-500',
  'from-emerald-700 via-teal-500 to-lime-300',
  'from-rose-700 via-fuchsia-600 to-violet-500',
  'from-amber-600 via-orange-500 to-red-500',
  'from-slate-900 via-blue-900 to-emerald-500'
];

const getCourseTitle = (course) => course?.name || course?.title || 'Untitled Course';

const getCourseInitials = (title) => title
  .split(/\s+/)
  .filter(Boolean)
  .slice(0, 3)
  .map((word) => word[0])
  .join('')
  .toUpperCase();

const getThemeForCourse = (course) => {
  const title = getCourseTitle(course);
  const index = title.split('').reduce((sum, char) => sum + char.charCodeAt(0), 0) % bannerThemes.length;
  return bannerThemes[index];
};

const normalizeCourses = (courses) => courses
  .map((course) => ({
    ...course,
    title: getCourseTitle(course),
    unitCount: Number(course.unitCount || course.unitsCount || course.Units?.length || 0),
    campusName: course.campus?.name || course.Campus?.name || 'Online campus',
    campusLocation: course.campus?.location || course.Campus?.location || 'Flexible access'
  }))
  .sort((a, b) => a.title.localeCompare(b.title, undefined, { sensitivity: 'base' }));

const CourseBanner = ({ course, compact = false }) => {
  const [imageFailed, setImageFailed] = useState(false);
  const resolvedImageUrl = getImageUrl(course.imageUrl);

  if (resolvedImageUrl && !imageFailed) {
    return (
      <img
        src={resolvedImageUrl}
        alt={course.title}
        className="h-full w-full object-cover"
        onError={() => setImageFailed(true)}
      />
    );
  }

  return (
    <div className={`relative h-full w-full overflow-hidden bg-gradient-to-br ${getThemeForCourse(course)}`}>
      <div className="absolute inset-0 opacity-20 [background-image:linear-gradient(135deg,rgba(255,255,255,.55)_1px,transparent_1px)] [background-size:18px_18px]" />
      <div className="absolute left-4 top-4 rounded-md bg-white/95 px-3 py-1 text-[10px] font-black uppercase tracking-wide text-slate-900">
        {course.selfPacedEnabled ? 'Self paced' : 'Instructor led'}
      </div>
      <div className="relative flex h-full items-end p-4">
        <div>
          <div className={`${compact ? 'text-2xl' : 'text-4xl'} font-black text-white`}>
            {getCourseInitials(course.title)}
          </div>
          <div className="mt-2 h-1 w-16 rounded-full bg-white/80" />
        </div>
      </div>
    </div>
  );
};

const CourseCard = ({ course, onStart }) => (
  <article className="group overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm transition-all hover:-translate-y-0.5 hover:border-blue-300 hover:shadow-md">
    <div className="h-44 overflow-hidden bg-slate-100">
      <CourseBanner course={course} />
    </div>
    <div className="p-4">
      <div className="mb-3 flex items-center gap-2 text-xs font-medium text-slate-500">
        <Building2 className="h-3.5 w-3.5 text-blue-600" />
        <span className="truncate">{course.campusName}</span>
      </div>
      <h3 className="min-h-[3rem] text-base font-bold leading-snug text-slate-950 line-clamp-2">
        {course.title}
      </h3>
      <p className="mt-2 min-h-[2.5rem] text-sm leading-5 text-slate-600 line-clamp-2">
        {course.description || 'A guided learning pathway from your institution catalog.'}
      </p>
      <div className="mt-4 flex flex-wrap items-center gap-3 text-xs text-slate-600">
        <span className="inline-flex items-center gap-1">
          <BookOpen className="h-3.5 w-3.5 text-emerald-600" />
          {course.unitCount || 'Open'} units
        </span>
        <span className="inline-flex items-center gap-1">
          <Clock className="h-3.5 w-3.5 text-amber-500" />
          {course.estimatedDuration ? `${course.estimatedDuration} weeks` : 'Flexible pace'}
        </span>
      </div>
      <div className="mt-4 flex items-center justify-between border-t border-slate-100 pt-4">
        <div className="inline-flex items-center gap-1 text-xs font-bold text-slate-700">
          <Star className="h-3.5 w-3.5 fill-amber-400 text-amber-400" />
          Institution course
        </div>
        <button
          onClick={() => onStart(course)}
          className="inline-flex h-9 items-center gap-1 rounded-md bg-blue-600 px-3 text-xs font-bold text-white transition-colors hover:bg-blue-700"
        >
          Enroll
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>
    </div>
  </article>
);

const PreviewCourseCarousel = ({ courses, activeIndex, onIndexChange, onStart }) => {
  const course = courses[activeIndex] || courses[0];
  const units = course?.units || course?.Units || [];

  if (!course) return null;

  const goToPrevious = () => {
    onIndexChange((activeIndex - 1 + courses.length) % courses.length);
  };

  const goToNext = () => {
    onIndexChange((activeIndex + 1) % courses.length);
  };

  return (
    <section id="preview-courses" className="mx-auto max-w-7xl px-4 pb-8 md:px-6">
      <div className="mb-4 flex flex-col gap-2 md:flex-row md:items-end md:justify-between">
        <div>
          <div className="mb-2 inline-flex w-fit items-center gap-2 rounded-full bg-blue-50 px-3 py-1 text-xs font-black uppercase tracking-wide text-blue-700">
            <CheckCircle2 className="h-3.5 w-3.5" />
            Admin selected
          </div>
          <h2 className="text-2xl font-black text-slate-950">Preview courses</h2>
          <p className="mt-1 max-w-2xl text-sm leading-6 text-slate-600">
            Browse highlighted courses with left and right controls before moving into the full catalog.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={goToPrevious}
            className="flex h-10 w-10 items-center justify-center rounded-md border border-slate-300 bg-white text-slate-700 hover:border-blue-400 hover:text-blue-700"
            aria-label="Previous preview course"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
          <span className="min-w-[72px] text-center text-xs font-black uppercase tracking-wide text-slate-500">
            {activeIndex + 1} / {courses.length}
          </span>
          <button
            onClick={goToNext}
            className="flex h-10 w-10 items-center justify-center rounded-md border border-slate-300 bg-white text-slate-700 hover:border-blue-400 hover:text-blue-700"
            aria-label="Next preview course"
          >
            <ChevronRight className="h-5 w-5" />
          </button>
        </div>
      </div>

      <article className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
        <div className="grid lg:grid-cols-[1.15fr_.85fr]">
          <div className="h-[280px] md:h-[360px]">
            <CourseBanner course={course} />
          </div>
          <div className="flex flex-col justify-between p-5 md:p-7">
            <div>
              <div className="mb-3 flex flex-wrap items-center gap-2">
                <span className="rounded-full bg-blue-600 px-3 py-1 text-[11px] font-black uppercase tracking-wide text-white">
                  Featured preview
                </span>
                <span className="rounded-full bg-slate-100 px-3 py-1 text-[11px] font-black uppercase tracking-wide text-slate-700">
                  {course.selfPacedEnabled ? 'Self paced' : 'Instructor led'}
                </span>
              </div>
              <h3 className="text-3xl font-black leading-tight text-slate-950">
                {course.title}
              </h3>
              <p className="mt-4 text-sm leading-6 text-slate-600">
                {course.description || 'Preview this course pathway and start enrollment from the landing page.'}
              </p>
            </div>

            <div className="mt-6 grid grid-cols-2 gap-3 text-sm">
              <div className="rounded-md bg-slate-50 p-3">
                <Building2 className="mb-2 h-4 w-4 text-blue-600" />
                <div className="font-bold text-slate-900">{course.campusName}</div>
                <div className="mt-1 text-xs text-slate-500">{course.campusLocation}</div>
              </div>
              <div className="rounded-md bg-slate-50 p-3">
                <BookOpen className="mb-2 h-4 w-4 text-emerald-600" />
                <div className="font-bold text-slate-900">{units.length || course.unitCount || 'Open'} units</div>
                <div className="mt-1 text-xs text-slate-500">
                  {course.estimatedDuration ? `${course.estimatedDuration} weeks` : 'Units auto hidden'}
                </div>
              </div>
            </div>

            <div className="mt-6 flex flex-wrap gap-3 border-t border-slate-100 pt-5">
              <button
                onClick={() => onStart(course)}
                className="inline-flex h-10 items-center gap-2 rounded-md bg-blue-600 px-4 text-sm font-bold text-white hover:bg-blue-700"
              >
                Enroll in course
                <ArrowRight className="h-4 w-4" />
              </button>
              <button
                onClick={goToNext}
                className="inline-flex h-10 items-center gap-2 rounded-md border border-slate-300 bg-white px-4 text-sm font-bold text-slate-800 hover:border-blue-400 hover:text-blue-700"
              >
                Browse next
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      </article>

      {courses.length > 1 && (
        <div className="mt-3 flex gap-2 overflow-x-auto pb-1">
          {courses.map((previewCourse, index) => (
            <button
              key={previewCourse.id}
              onClick={() => onIndexChange(index)}
              className={`h-2.5 rounded-full transition-all ${
                index === activeIndex ? 'w-9 bg-blue-600' : 'w-2.5 bg-slate-300 hover:bg-slate-400'
              }`}
              aria-label={`Show ${previewCourse.title}`}
            />
          ))}
        </div>
      )}
    </section>
  );
};

const CatalogCourseTile = ({ course, onStart }) => (
  <article className="overflow-hidden rounded-lg bg-white shadow-sm">
    <div className="h-28">
      <CourseBanner course={course} compact />
    </div>
    <div className="p-4">
      <div className="mb-2 flex items-center gap-2 text-[11px] font-semibold text-slate-500">
        <Building2 className="h-3.5 w-3.5 text-blue-600" />
        <span className="truncate">{course.campusName}</span>
      </div>
      <h3 className="min-h-[2.5rem] text-sm font-black leading-5 text-slate-950 line-clamp-2">{course.title}</h3>
      <div className="mt-3 flex items-center justify-between text-xs text-slate-600">
        <span>{course.unitCount || 'Open'} units</span>
        <button
          onClick={() => onStart(course)}
          className="font-black text-blue-700 hover:text-blue-800"
        >
          Enroll
        </button>
      </div>
    </div>
  </article>
);

const Landing = () => {
  const navigate = useNavigate();
  const [institution, setInstitution] = useState(null);
  const [institutionColors, setInstitutionColors] = useState(['#0f766e', '#2563eb']);
  const [courses, setCourses] = useState([]);
  const [previewCourses, setPreviewCourses] = useState([]);
  const [activePreviewIndex, setActivePreviewIndex] = useState(0);
  const [showAllCatalog, setShowAllCatalog] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeFilter, setActiveFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadCourses();
  }, []);

  useEffect(() => {
    setShowAllCatalog(false);
  }, [searchTerm, activeFilter]);

  const loadCourses = async () => {
    try {
      setLoading(true);
      setError('');

      const selectedInstitutionString = localStorage.getItem('selectedInstitution');
      const selectedInstitution = selectedInstitutionString ? JSON.parse(selectedInstitutionString) : null;

      if (selectedInstitution) {
        setInstitution(selectedInstitution);
        setInstitutionColors([
          selectedInstitution.primaryColor || '#0f766e',
          selectedInstitution.secondaryColor || '#2563eb'
        ]);
      }

      const institutionQuery = selectedInstitution?.id ? `?institutionId=${selectedInstitution.id}` : '';
      const response = await fetch(`${API_BASE_URL}/courses/public/all${institutionQuery}`);

      if (!response.ok) {
        throw new Error('Unable to load the course catalog.');
      }

      const data = await response.json();
      setCourses(normalizeCourses(Array.isArray(data) ? data : []));

      if (selectedInstitution?.id) {
        const landingResponse = await fetch(`${API_BASE_URL}/landing-page/${selectedInstitution.id}`);
        if (landingResponse.ok) {
          const landingData = await landingResponse.json();
          const normalizedPreviewCourses = normalizeCourses(landingData.previewCourses || [])
            .map((course) => ({
              ...course,
              units: course.units || course.Units || []
            }));
          setPreviewCourses(normalizedPreviewCourses);
          setActivePreviewIndex(0);
        }
      }
    } catch (err) {
      console.error('Error loading public courses:', err);
      setError(err.message || 'Unable to load the course catalog.');
      setCourses([]);
      setPreviewCourses([]);
    } finally {
      setLoading(false);
    }
  };

  const filteredCourses = useMemo(() => {
    const search = searchTerm.trim().toLowerCase();

    return courses.filter((course) => {
      const matchesSearch = !search
        || course.title.toLowerCase().includes(search)
        || (course.description || '').toLowerCase().includes(search)
        || course.campusName.toLowerCase().includes(search);

      const matchesFilter = activeFilter === 'all'
        || (activeFilter === 'self-paced' && course.selfPacedEnabled)
        || (activeFilter === 'instructor-led' && !course.selfPacedEnabled);

      return matchesSearch && matchesFilter;
    });
  }, [courses, searchTerm, activeFilter]);

  const featuredCourses = filteredCourses.slice(0, 4);
  const catalogCourses = filteredCourses.slice(4);
  const visibleCatalogCourses = showAllCatalog ? catalogCourses : catalogCourses.slice(0, 9);
  const selfPacedCount = courses.filter((course) => course.selfPacedEnabled).length;
  const campusNames = [...new Set(courses.map((course) => course.campusName).filter(Boolean))];
  const institutionName = institution?.name || 'Intuit Online Academy';

  const handleStartCourse = (course) => {
    localStorage.setItem('selectedCourse', JSON.stringify(course));
    navigate('/register');
  };

  return (
    <main className="min-h-screen bg-white text-slate-950">
      <header className="sticky top-0 z-40 border-b border-slate-200 bg-white/95 backdrop-blur">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 md:px-6">
          <button
            onClick={() => navigate('/select-institution')}
            className="flex items-center gap-2 text-left"
          >
            <span
              className="flex h-9 w-9 items-center justify-center rounded-md text-white"
              style={{ background: `linear-gradient(135deg, ${institutionColors[0]}, ${institutionColors[1]})` }}
            >
              <GraduationCap className="h-5 w-5" />
            </span>
            <span className="min-w-0">
              <span className="block text-lg font-black leading-5 text-blue-700">intuit</span>
              <span className="block max-w-[180px] truncate text-[10px] font-bold uppercase tracking-wide text-slate-500">
                {institutionName}
              </span>
            </span>
          </button>

          <div className="hidden w-full max-w-lg items-center rounded-full border border-slate-300 bg-white px-4 py-2 md:flex">
            <Search className="mr-2 h-4 w-4 text-slate-400" />
            <input
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              placeholder="What do you want to learn?"
              className="w-full border-0 bg-transparent text-sm outline-none placeholder:text-slate-400"
            />
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => navigate('/login')}
              className="hidden h-9 items-center gap-2 rounded-md px-3 text-sm font-semibold text-blue-700 hover:bg-blue-50 sm:inline-flex"
            >
              <LogIn className="h-4 w-4" />
              Log in
            </button>
            <button
              onClick={() => navigate('/register')}
              className="h-9 rounded-md border border-blue-600 px-3 text-sm font-bold text-blue-700 hover:bg-blue-50"
            >
              Join for Free
            </button>
          </div>
        </div>
      </header>

      <section className="border-b border-slate-100 bg-slate-50">
        <div className="mx-auto grid max-w-7xl gap-4 px-4 py-5 md:grid-cols-[1.05fr_.95fr] md:px-6">
          <div className="overflow-hidden rounded-lg bg-[#fff1cf]">
            <div className="grid h-full min-h-[250px] md:grid-cols-[1fr_.95fr]">
              <div className="flex flex-col justify-center p-6 md:p-8">
                <div className="mb-4 inline-flex w-fit items-center gap-2 rounded-md bg-white px-3 py-1 text-xs font-black uppercase tracking-wide text-blue-700">
                  <Sparkles className="h-3.5 w-3.5" />
                  Sorted course catalog
                </div>
                <h1 className="text-3xl font-black leading-tight text-slate-950 md:text-4xl">
                  Learn from every course available at {institutionName}
                </h1>
                <p className="mt-4 max-w-xl text-sm leading-6 text-slate-700 md:text-base">
                  Browse the institution catalog in one clean front page, organized alphabetically by course title for faster discovery.
                </p>
                <div className="mt-6 flex flex-wrap gap-3">
                  <button
                    onClick={() => navigate('/register')}
                    className="inline-flex h-11 items-center gap-2 rounded-md bg-blue-600 px-5 text-sm font-bold text-white hover:bg-blue-700"
                  >
                    Start learning
                    <ArrowRight className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => navigate('/login?role=lecturer')}
                    className="inline-flex h-11 items-center gap-2 rounded-md border border-slate-300 bg-white px-5 text-sm font-bold text-slate-800 hover:border-slate-400"
                  >
                    Lecturer login
                  </button>
                </div>
              </div>
              <div className="relative min-h-[220px] overflow-hidden bg-gradient-to-br from-blue-700 via-cyan-500 to-emerald-300 p-6">
                <div className="absolute inset-0 opacity-20 [background-image:linear-gradient(135deg,rgba(255,255,255,.65)_1px,transparent_1px)] [background-size:20px_20px]" />
                <div className="relative flex h-full flex-col justify-end">
                  <div className="w-fit rounded-md bg-white px-4 py-3 shadow-sm">
                    <div className="text-4xl font-black text-orange-600">{courses.length || '0'}+</div>
                    <div className="text-xs font-bold uppercase tracking-wide text-slate-700">available courses</div>
                  </div>
                  <div className="mt-4 flex gap-2">
                    <span className="rounded-full bg-white/90 px-3 py-1 text-xs font-bold text-slate-900">Catalog</span>
                    <span className="rounded-full bg-white/90 px-3 py-1 text-xs font-bold text-slate-900">Enrollment</span>
                    <span className="rounded-full bg-white/90 px-3 py-1 text-xs font-bold text-slate-900">LMS</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="overflow-hidden rounded-lg bg-gradient-to-br from-violet-700 via-blue-700 to-slate-950 text-white">
            <div className="flex h-full min-h-[250px] flex-col justify-between p-6 md:p-8">
              <div>
                <div className="text-sm font-bold text-white/75">{institutionName} for students</div>
                <h2 className="mt-4 max-w-md text-3xl font-black leading-tight">
                  Find your next course without digging through admin notices.
                </h2>
                <p className="mt-4 max-w-lg text-sm leading-6 text-white/80">
                  Search by title, compare learning modes, then enroll directly into the pathway that fits your schedule.
                </p>
              </div>
              <div className="mt-6 grid grid-cols-3 gap-3">
                <div className="rounded-md bg-white/10 p-3">
                  <Library className="mb-2 h-5 w-5 text-cyan-200" />
                  <div className="text-lg font-black">{courses.length}</div>
                  <div className="text-[10px] font-bold uppercase text-white/70">Courses</div>
                </div>
                <div className="rounded-md bg-white/10 p-3">
                  <Clock className="mb-2 h-5 w-5 text-amber-200" />
                  <div className="text-lg font-black">{selfPacedCount}</div>
                  <div className="text-[10px] font-bold uppercase text-white/70">Self paced</div>
                </div>
                <div className="rounded-md bg-white/10 p-3">
                  <Building2 className="mb-2 h-5 w-5 text-emerald-200" />
                  <div className="text-lg font-black">{campusNames.length || 1}</div>
                  <div className="text-[10px] font-bold uppercase text-white/70">Campuses</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-8 md:px-6">
        <div className="mb-5 md:hidden">
          <div className="flex items-center rounded-full border border-slate-300 bg-white px-4 py-3">
            <Search className="mr-2 h-4 w-4 text-slate-400" />
            <input
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              placeholder="Search courses"
              className="w-full border-0 bg-transparent text-sm outline-none placeholder:text-slate-400"
            />
          </div>
        </div>

        <div className="mb-5 flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
          <div>
            <h2 className="text-2xl font-black text-slate-950">New and popular</h2>
            <p className="mt-1 text-sm text-slate-600">Alphabetized courses from the selected institution catalog.</p>
          </div>
          <div className="flex flex-wrap gap-2">
            {[
              { id: 'all', label: 'Most popular' },
              { id: 'self-paced', label: 'Self paced' },
              { id: 'instructor-led', label: 'Instructor led' }
            ].map((filter) => (
              <button
                key={filter.id}
                onClick={() => setActiveFilter(filter.id)}
                className={`h-9 rounded-full border px-4 text-xs font-bold transition-colors ${
                  activeFilter === filter.id
                    ? 'border-slate-950 bg-slate-950 text-white'
                    : 'border-slate-300 bg-white text-slate-700 hover:border-blue-500 hover:text-blue-700'
                }`}
              >
                {filter.label}
              </button>
            ))}
          </div>
        </div>

        {loading && (
          <div className="flex min-h-[300px] items-center justify-center rounded-lg border border-slate-200 bg-slate-50">
            <div className="text-center">
              <Loader2 className="mx-auto h-9 w-9 animate-spin text-blue-600" />
              <p className="mt-3 text-sm font-semibold text-slate-600">Loading available courses...</p>
            </div>
          </div>
        )}

        {!loading && error && (
          <div className="rounded-lg border border-red-200 bg-red-50 p-6 text-center">
            <p className="font-bold text-red-700">{error}</p>
            <button
              onClick={loadCourses}
              className="mt-4 rounded-md bg-red-600 px-4 py-2 text-sm font-bold text-white hover:bg-red-700"
            >
              Try again
            </button>
          </div>
        )}

        {!loading && !error && filteredCourses.length === 0 && (
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-10 text-center">
            <Search className="mx-auto h-10 w-10 text-slate-400" />
            <h3 className="mt-4 text-lg font-black text-slate-900">No courses found</h3>
            <p className="mt-2 text-sm text-slate-600">Adjust your search or filter to see more courses.</p>
          </div>
        )}

        {!loading && !error && filteredCourses.length > 0 && (
          <>
            <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
              {featuredCourses.map((course) => (
                <CourseCard key={course.id} course={course} onStart={handleStartCourse} />
              ))}
            </div>

            {filteredCourses.length > 4 && (
              <div className="mt-5">
                <button
                  onClick={() => document.getElementById(previewCourses.length ? 'preview-courses' : 'all-courses')?.scrollIntoView({ behavior: 'smooth' })}
                  className="rounded-md bg-blue-600 px-5 py-3 text-sm font-bold text-white hover:bg-blue-700"
                >
                  Show {filteredCourses.length - 4} more
                </button>
              </div>
            )}
          </>
        )}
      </section>

      {!loading && !error && previewCourses.length > 0 && (
        <PreviewCourseCarousel
          courses={previewCourses}
          activeIndex={activePreviewIndex}
          onIndexChange={setActivePreviewIndex}
          onStart={handleStartCourse}
        />
      )}

      {!loading && !error && catalogCourses.length > 0 && (
        <section id="all-courses" className="mx-auto max-w-7xl px-4 pb-10 md:px-6">
          <div className="rounded-lg bg-gradient-to-r from-blue-600 via-sky-500 to-emerald-400 p-4 md:p-6">
            <div className="grid gap-5 md:grid-cols-[240px_1fr]">
              <div className="flex flex-col justify-center text-white">
                <div className="mb-3 inline-flex w-fit items-center gap-2 rounded-full bg-white/20 px-3 py-1 text-xs font-black uppercase tracking-wide">
                  <CheckCircle2 className="h-3.5 w-3.5" />
                  Career ready
                </div>
                <h2 className="text-2xl font-black leading-tight">Explore the full catalog</h2>
                <p className="mt-3 text-sm leading-6 text-white/85">
                  Course titles remain sorted alphabetically so learners can scan quickly.
                </p>
                {catalogCourses.length > 9 && (
                  <button
                    onClick={() => setShowAllCatalog((current) => !current)}
                    className="mt-5 inline-flex h-10 w-fit items-center rounded-md bg-white px-4 text-sm font-black text-blue-700 hover:bg-blue-50"
                  >
                    {showAllCatalog ? 'Show less' : `Show all ${catalogCourses.length}`}
                  </button>
                )}
              </div>
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {visibleCatalogCourses.map((course) => (
                  <CatalogCourseTile
                    key={course.id}
                    course={course}
                    onStart={handleStartCourse}
                  />
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      <section id="campus-partners" className="border-t border-slate-200 bg-slate-50">
        <div className="mx-auto max-w-7xl px-4 py-8 md:px-6">
          <h2 className="mb-4 text-base font-black text-slate-950">
            Learn from {campusNames.length || 1} campus partner{campusNames.length === 1 ? '' : 's'}
          </h2>
          <div className="flex gap-3 overflow-x-auto pb-2">
            {(campusNames.length ? campusNames : ['Online campus']).map((campus) => (
              <div key={campus} className="inline-flex shrink-0 items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2">
                <Building2 className="h-4 w-4 text-blue-600" />
                <span className="text-xs font-bold text-slate-800">{campus}</span>
              </div>
            ))}
          </div>
          <div className="mt-7 grid gap-4 md:grid-cols-3">
            {[
              { icon: GraduationCap, title: 'Launch a new course path' },
              { icon: Users, title: 'Join your institution community' },
              { icon: BookOpen, title: 'Study from one LMS catalog' }
            ].map((item) => (
              <div key={item.title} className="flex items-center justify-between rounded-lg bg-white p-5 shadow-sm">
                <div className="font-black text-slate-900">{item.title}</div>
                <item.icon className="h-7 w-7 text-blue-600" />
              </div>
            ))}
          </div>
        </div>
      </section>

      <footer className="border-t border-slate-200 bg-white">
        <div className="mx-auto flex max-w-7xl flex-col gap-4 px-4 py-5 md:flex-row md:items-center md:justify-between md:px-6">
          <div className="flex items-center gap-2">
            <span
              className="flex h-9 w-9 items-center justify-center rounded-md text-white"
              style={{ background: `linear-gradient(135deg, ${institutionColors[0]}, ${institutionColors[1]})` }}
            >
              <GraduationCap className="h-5 w-5" />
            </span>
            <div>
              <div className="text-lg font-black leading-5 text-blue-700">intuit</div>
              <div className="max-w-[260px] truncate text-[10px] font-bold uppercase tracking-wide text-slate-500">
                {institutionName}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3 text-xs font-bold text-slate-600 sm:flex sm:items-center sm:gap-5">
            <button onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} className="text-left hover:text-blue-700">
              Catalog
            </button>
            <button onClick={() => navigate('/login')} className="text-left hover:text-blue-700">
              Student Login
            </button>
            <button onClick={() => navigate('/login?role=lecturer')} className="text-left hover:text-blue-700">
              Lecturer Login
            </button>
          </div>

          <div className="text-xs font-semibold text-slate-500">
            {courses.length} courses available
          </div>
        </div>
      </footer>
    </main>
  );
};

export default Landing;
